import Swal from "sweetalert2";
import React, { useState } from "react";
import { createPortal } from "react-dom";
import LayersIcon from '@mui/icons-material/Layers';
import ScienceIcon from '@mui/icons-material/Science';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import AutorenewIcon from '@mui/icons-material/Autorenew';

/**
 * ReprocesoSection (Sección VI - Bitácora de Envasado)
 * Maneja la elaboración de clases, reprocesos y reacondicionamientos de mezcal
 * a partir de los inventarios a granel de Envasado conforme a la NOM-070-SCFI-2016.
 */
const ReprocesoSection = ({
  reprocesoRecords = [],
  granelRecords = [],
  allGranelRecords = [],
  isFormOpen,
  setIsFormOpen,
  formData = {},
  setFormData,
  handleSaveReproceso,
  handleDeleteReproceso
}) => {
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");

  // Lista de graneles disponibles de Envasado para alimentar el selector
  const availableGraneles = (Array.isArray(allGranelRecords) && allGranelRecords.length > 0)
    ? allGranelRecords
    : (Array.isArray(granelRecords) ? granelRecords : []);

  /**
   * Define la Clase Final Obtenida según la NOM-070-SCFI-2016 con base en el Proceso Realizado:
   * - “ABOCADO CON” -> “Abocado con”
   * - “DESTILADO CON” -> “Destilado con”
   * - “REPROCESO O REACONDICIONAMIENTO” -> Clase origen o “Blanco Joven”
   */
  const obtenerClaseFinalSegunProceso = (proceso, claseOrigen = "Blanco Joven") => {
    const p = String(proceso || "").toUpperCase().trim();
    if (p.includes("ABOCADO")) {
      return "Abocado con";
    }
    if (p.includes("DESTILADO")) {
      return "Destilado con";
    }
    if (p.includes("REPROCESO") || p.includes("REACONDICIONAMIENTO")) {
      return claseOrigen || "Blanco Joven";
    }
    return "Blanco Joven";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "procesoRealizado") {
      const autoClase = obtenerClaseFinalSegunProceso(value, formData?.claseOrigen);
      setFormData(prev => ({
        ...prev,
        procesoRealizado: value,
        claseSalida: autoClase
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Auto-fill al seleccionar un lote a granel de Envasado
  const handleLoteOrigenChange = (e) => {
    const val = e.target.value;
    const found = availableGraneles.find(g =>
      String(g.id_bitacora_granel) === String(val) ||
      String(g.id) === String(val) ||
      String(g.dbId) === String(val) ||
      String(g.noLote || g.no_lote) === String(val)
    );

    if (found) {
      const loteNo = found.noLote || found.no_lote || "";
      const vol = found._vDisp ?? (parseFloat(String(found.invFinalVol ?? found.saldoFinal ?? found.litros ?? found.volumen ?? 0).replace(/[^\d.]/g, '')) || 0);
      const alc = found._aDisp ?? (parseFloat(String(found.porcentaje_alc_vol ?? found.invFinalAlc ?? 0).replace(/[^\d.]/g, '')) || 0);
      const cat = found.categoria || "Mezcal Artesanal";
      const cls = found.clase || "Blanco Joven";
      const mag = found.tipoMaguey || found.especie || "";
      const anl = found.clave_fq || found.noAnalisis || found.noAnalisisFisicoquimico || "";
      const ing = found.ingredientes || found.ingrediente || "Sin ingredientes adicionales";
      const edd = found.edad || "Sin edad / 0 meses";
      const granelId = found.id_bitacora_granel || found.id || found.dbId || null;

      const autoClase = obtenerClaseFinalSegunProceso(formData?.procesoRealizado || "REPROCESO O REACONDICIONAMIENTO", cls);

      setFormData(prev => ({
        ...prev,
        id_bitacora_granel: granelId,
        loteOrigen: loteNo,
        volumenOrigen: vol || prev.volumenOrigen || "",
        pctAlcOrigen: alc || prev.pctAlcOrigen || "",
        categoriaOrigen: cat || prev.categoriaOrigen || "Mezcal Artesanal",
        claseOrigen: cls || prev.claseOrigen || "Blanco Joven",
        tipoMagueyOrigen: mag || prev.tipoMagueyOrigen || "",
        analisisOrigen: anl || prev.analisisOrigen || "",
        ingredientesOrigen: ing || prev.ingredientesOrigen || "",
        edadOrigen: edd || prev.edadOrigen || "",
        loteSalida: prev.loteSalida || (loteNo ? `${loteNo}-REP` : ""),
        claseSalida: autoClase
      }));
    } else {
      setFormData(prev => ({ ...prev, loteOrigen: val, id_bitacora_granel: null }));
    }
  };

  const handleNewRecord = () => {
    const today = new Date().toISOString().split("T")[0];
    const defaultProceso = "REPROCESO O REACONDICIONAMIENTO";
    setFormData({
      id: `REP-${Date.now()}`,
      fechaIngreso: today,
      loteOrigen: "",
      volumenOrigen: "",
      pctAlcOrigen: "",
      categoriaOrigen: "Mezcal Artesanal",
      claseOrigen: "Blanco Joven",
      tipoMagueyOrigen: "",
      analisisOrigen: "",
      ingredientesOrigen: "",
      edadOrigen: "",
      procesoRealizado: defaultProceso,
      ingredientesAdicionados: "",
      descripcionProceso: "",
      fechaSalida: today,
      loteSalida: "",
      volumenSalida: "",
      pctAlcSalida: "",
      claseSalida: "Blanco Joven",
      observaciones: "",
      status: "enviada"
    });
    setIsFormOpen(true);
  };

  const handleEditRecord = (rec) => {
    setFormData({
      ...rec,
      loteOrigen: rec.loteOrigen || rec.lote_origen || "",
      volumenOrigen: rec.volumenOrigen || rec.volumen_origen || "",
      pctAlcOrigen: rec.pctAlcOrigen || rec.pct_alc_origen || "",
      categoriaOrigen: rec.categoriaOrigen || rec.categoria_origen || "Mezcal Artesanal",
      claseOrigen: rec.claseOrigen || rec.clase_origen || "Blanco Joven",
      tipoMagueyOrigen: rec.tipoMagueyOrigen || rec.tipo_maguey_origen || "",
      analisisOrigen: rec.analisisOrigen || rec.analisis_origen || "",
      ingredientesOrigen: rec.ingredientesOrigen || rec.ingredientes_origen || "",
      edadOrigen: rec.edadOrigen || rec.edad_origen || "",
      procesoRealizado: rec.procesoRealizado || rec.proceso_realizado || "REPROCESO O REACONDICIONAMIENTO",
      ingredientesAdicionados: rec.ingredientesAdicionados || rec.ingredientes_adicionados || "",
      descripcionProceso: rec.descripcionProceso || rec.descripcion_proceso || "",
      fechaIngreso: rec.fechaIngreso || rec.fecha_ingreso || "",
      fechaSalida: rec.fechaSalida || rec.fecha_salida || "",
      loteSalida: rec.loteSalida || rec.lote_salida || "",
      volumenSalida: rec.volumenSalida || rec.volumen_salida || "",
      pctAlcSalida: rec.pctAlcSalida || rec.pct_alc_salida || "",
      claseSalida: rec.claseSalida || rec.clase_salida || "",
      observaciones: rec.observaciones || ""
    });
    setIsFormOpen(true);
  };

  const handleSubmit = (e, statusToApply = "enviada") => {
    e.preventDefault();
    if (!formData.loteOrigen) {
      Swal.fire("Lote requerido", "Debe seleccionar un lote de granel de origen.", "warning");
      return;
    }
    if (!formData.loteSalida) {
      Swal.fire("Lote resultante requerido", "Debe especificar el número de lote final obtenido.", "warning");
      return;
    }

    const payload = {
      ...formData,
      status: statusToApply,
      estatus: statusToApply
    };

    if (handleSaveReproceso) {
      handleSaveReproceso(payload);
    }
    setIsFormOpen(false);
  };

  // Filtrado de registros
  const filteredRecords = reprocesoRecords.filter(rec => {
    const lotOrig = String(rec.loteOrigen || rec.lote_origen || "").toLowerCase();
    const lotSal = String(rec.loteSalida || rec.lote_salida || "").toLowerCase();
    const proc = String(rec.procesoRealizado || rec.proceso_realizado || "").toLowerCase();
    const ing = String(rec.ingredientesAdicionados || rec.ingredientes_adicionados || "").toLowerCase();
    const sTerm = search.toLowerCase();

    const matchesSearch = lotOrig.includes(sTerm) || lotSal.includes(sTerm) || proc.includes(sTerm) || ing.includes(sTerm);

    const st = String(rec.status || rec.estatus || "").toLowerCase();
    let matchesStatus = true;
    if (statusFilter === "borrador") matchesStatus = st === "borrador";
    else if (statusFilter === "revision") matchesStatus = st === "revision" || st === "en_revision" || st === "enviada";
    else if (statusFilter === "aprobado") matchesStatus = st.includes("aprob") || st.includes("acept");
    else if (statusFilter === "rechazado") matchesStatus = st.includes("rechaz");

    return matchesSearch && matchesStatus;
  });

  // Métricas
  const totalLitros = reprocesoRecords.reduce((acc, r) => acc + (parseFloat(r.volumenSalida || r.volumen_salida || 0) || 0), 0);
  const aprobadosCount = reprocesoRecords.filter(r => String(r.status || r.estatus || "").toLowerCase().includes("aprob") || String(r.status || r.estatus || "").toLowerCase().includes("acept")).length;
  const revisionCount = reprocesoRecords.filter(r => {
    const s = String(r.status || r.estatus || "").toLowerCase();
    return s === "enviada" || s === "revision" || s === "en_revision";
  }).length;

  return (
    <div className="animate-fadeIn">
      {/* HEADER DE SECCIÓN */}
      <div className="history-section-header" style={{ marginBottom: "20px" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
            <span className="badge-modal-tag" style={{ background: "rgba(124, 58, 237, 0.12)", color: "#7C3AED", fontWeight: 700 }}>
              SECCIÓN VI • ENVASADO
            </span>
            <span className="badge-modal-tag" style={{ background: "#EDE9FE", color: "#6D28D9", fontWeight: 600 }}>
              NOM-070-SCFI-2016
            </span>
          </div>
          <h2 style={{ margin: 0, fontSize: "22px", fontWeight: 800, color: "#1E293B" }}>
            Reproceso & Elaboración de Clases
          </h2>
          <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "#64748B" }}>
            Control de abocado con, destilado con y reprocesos o reacondicionamientos con inventarios de granel de envasado
          </p>
        </div>

        <button
          onClick={handleNewRecord}
          className="btn-add-new"
          style={{ background: "linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)", boxShadow: "0 4px 12px rgba(124, 58, 237, 0.3)" }}
        >
          <AddIcon style={{ fontSize: 18 }} />
          <span>+ Nuevo Reproceso</span>
        </button>
      </div>

      {/* METRIC CARDS */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: "14px", marginBottom: "22px" }}>
        <div style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "14px", padding: "16px", display: "flex", alignItems: "center", gap: "14px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <div style={{ width: "44px", height: "44px", borderRadius: "10px", background: "rgba(124, 58, 237, 0.1)", color: "#7C3AED", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <AutorenewIcon style={{ fontSize: 24 }} />
          </div>
          <div>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>Total Reprocesos</div>
            <div style={{ fontSize: "22px", fontWeight: 800, color: "#0F172A" }}>{reprocesoRecords.length}</div>
          </div>
        </div>

        <div style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "14px", padding: "16px", display: "flex", alignItems: "center", gap: "14px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <div style={{ width: "44px", height: "44px", borderRadius: "10px", background: "rgba(245, 158, 11, 0.1)", color: "#D97706", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <SettingsIcon style={{ fontSize: 24 }} />
          </div>
          <div>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>En Revisión UI</div>
            <div style={{ fontSize: "22px", fontWeight: 800, color: "#D97706" }}>{revisionCount}</div>
          </div>
        </div>

        <div style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "14px", padding: "16px", display: "flex", alignItems: "center", gap: "14px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <div style={{ width: "44px", height: "44px", borderRadius: "10px", background: "rgba(16, 185, 129, 0.1)", color: "#10B981", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CheckCircleIcon style={{ fontSize: 24 }} />
          </div>
          <div>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>Aprobados UI</div>
            <div style={{ fontSize: "22px", fontWeight: 800, color: "#10B981" }}>{aprobadosCount}</div>
          </div>
        </div>

        <div style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "14px", padding: "16px", display: "flex", alignItems: "center", gap: "14px", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <div style={{ width: "44px", height: "44px", borderRadius: "10px", background: "rgba(59, 130, 246, 0.1)", color: "#2563EB", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <WaterDropIcon style={{ fontSize: 24 }} />
          </div>
          <div>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>Volumen Obtenido</div>
            <div style={{ fontSize: "22px", fontWeight: 800, color: "#2563EB" }}>{totalLitros.toLocaleString()} L</div>
          </div>
        </div>
      </div>

      {/* SEARCH AND FILTERS */}
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: "12px", background: "#FFFFFF", padding: "14px 18px", borderRadius: "14px", border: "1px solid #E2E8F0", marginBottom: "18px" }}>
        <div style={{ position: "relative", minWidth: "280px", flex: 1 }}>
          <SearchIcon style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94A3B8", fontSize: 20 }} />
          <input
            type="text"
            placeholder="Buscar por lote origen, lote salida, proceso o ingredientes..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="granel-input-control"
            style={{ paddingLeft: "40px", height: "40px" }}
          />
        </div>

        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <FilterListIcon style={{ color: "#64748B", fontSize: 20 }} />
          {["todos", "revision", "aprobado", "borrador", "rechazado"].map(st => (
            <button
              key={st}
              type="button"
              onClick={() => setStatusFilter(st)}
              className={`segmented-pill-btn ${statusFilter === st ? "active" : ""}`}
              style={{ height: "34px", padding: "0 12px", fontSize: "12px", textTransform: "capitalize" }}
            >
              {st}
            </button>
          ))}
        </div>
      </div>

      {/* TABLA DE REGISTROS */}
      <div className="records-list-wrapper" style={{ background: "#FFFFFF", borderRadius: "16px", border: "1px solid #E2E8F0", overflow: "hidden", boxShadow: "0 2px 4px rgba(0,0,0,0.03)" }}>
        <table className="records-table">
          <thead>
            <tr>
              <th colSpan="5" style={{ textAlign: "center", background: "#F1F5F9", color: "#475569", borderRight: "1px solid #CBD5E1", fontSize: "11px", letterSpacing: "0.5px" }}>
                1. LOTE A GRANEL DE ORIGEN (ENVASADO)
              </th>
              <th colSpan="2" style={{ textAlign: "center", background: "rgba(124, 58, 237, 0.08)", color: "#7C3AED", borderRight: "1px solid #CBD5E1", fontSize: "11px", letterSpacing: "0.5px" }}>
                2. PROCESAMIENTO
              </th>
              <th colSpan="4" style={{ textAlign: "center", background: "rgba(16, 185, 129, 0.08)", color: "#059669", fontSize: "11px", letterSpacing: "0.5px" }}>
                3. RESULTADO DEL REPROCESO
              </th>
              <th rowSpan="2" style={{ textAlign: "center" }}>ESTATUS</th>
              <th rowSpan="2" style={{ textAlign: "center", width: "110px" }}>ACCIONES</th>
            </tr>
            <tr>
              <th>Fecha Ing.</th>
              <th>Lote Origen</th>
              <th>Volumen</th>
              <th>% Alc.</th>
              <th style={{ borderRight: "1px solid #CBD5E1" }}>Clase / Especie</th>
              <th>Proceso Realizado</th>
              <th style={{ borderRight: "1px solid #CBD5E1" }}>Ingredientes</th>
              <th>Lote Salida</th>
              <th>Volumen</th>
              <th>% Alc.</th>
              <th>Clase Salida (NOM-070)</th>
            </tr>
          </thead>
          <tbody>
            {filteredRecords.length === 0 ? (
              <tr>
                <td colSpan="13" style={{ textAlign: "center", padding: "40px", color: "#94A3B8" }}>
                  <AutorenewIcon style={{ fontSize: 40, opacity: 0.3, marginBottom: "8px", display: "block", margin: "0 auto 8px" }} />
                  No se encontraron registros de reprocesos en la bitácora de envasado.
                </td>
              </tr>
            ) : (
              filteredRecords.map((r, idx) => {
                const st = String(r.status || r.estatus || "enviada").toLowerCase();
                let badgeClass = "status-revision";
                let badgeText = "En revisión UI";
                if (st.includes("aprob") || st.includes("acept")) {
                  badgeClass = "status-aprobado";
                  badgeText = "Aprobado";
                } else if (st.includes("rechaz")) {
                  badgeClass = "status-rechazado";
                  badgeText = "Rechazado";
                } else if (st === "borrador") {
                  badgeClass = "status-borrador";
                  badgeText = "Borrador";
                }

                return (
                  <tr key={r.id || r.id_bitacora_reproceso || idx}>
                    <td>{r.fechaIngreso || r.fecha_ingreso || "-"}</td>
                    <td style={{ fontWeight: 700, color: "#1E293B" }}>{r.loteOrigen || r.lote_origen || "-"}</td>
                    <td>{Number(r.volumenOrigen || r.volumen_origen || 0).toLocaleString()} L</td>
                    <td>{r.pctAlcOrigen || r.pct_alc_origen || "0"}%</td>
                    <td style={{ borderRight: "1px solid #E2E8F0" }}>
                      <div style={{ fontWeight: 600 }}>{r.claseOrigen || r.clase_origen || "Blanco Joven"}</div>
                      <small style={{ color: "#64748B" }}>{r.tipoMagueyOrigen || r.tipo_maguey_origen || ""}</small>
                    </td>
                    <td>
                      <span className="badge-modal-tag" style={{ background: "#EDE9FE", color: "#6D28D9", fontWeight: 700 }}>
                        {r.procesoRealizado || r.proceso_realizado || "REPROCESO"}
                      </span>
                    </td>
                    <td style={{ borderRight: "1px solid #E2E8F0", maxWidth: "160px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {r.ingredientesAdicionados || r.ingredientes_adicionados || "Ninguno"}
                    </td>
                    <td style={{ fontWeight: 800, color: "#047857" }}>{r.loteSalida || r.lote_salida || "-"}</td>
                    <td style={{ fontWeight: 700 }}>{Number(r.volumenSalida || r.volumen_salida || 0).toLocaleString()} L</td>
                    <td>{r.pctAlcSalida || r.pct_alc_salida || "0"}%</td>
                    <td style={{ fontWeight: 700, color: "#7C3AED" }}>{r.claseSalida || r.clase_salida || "-"}</td>
                    <td>
                      <span className={`status-badge-envasado ${badgeClass}`}>
                        {badgeText}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                        <button
                          type="button"
                          onClick={() => setSelectedRecord(r)}
                          title="Ver Detalle"
                          style={{ background: "#F1F5F9", color: "#475569", border: "none", borderRadius: "8px", width: "32px", height: "32px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                        >
                          <VisibilityIcon style={{ fontSize: 16 }} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleEditRecord(r)}
                          title="Editar Registro"
                          style={{ background: "#EDE9FE", color: "#7C3AED", border: "none", borderRadius: "8px", width: "32px", height: "32px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                        >
                          <EditIcon style={{ fontSize: 16 }} />
                        </button>
                        {handleDeleteReproceso && (st === "borrador" || st.includes("rechaz")) && (
                          <button
                            type="button"
                            onClick={() => handleDeleteReproceso(r.id || r.id_bitacora_reproceso)}
                            title="Eliminar"
                            style={{ background: "#FEE2E2", color: "#DC2626", border: "none", borderRadius: "8px", width: "32px", height: "32px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <DeleteIcon style={{ fontSize: 16 }} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* =========================================================================
          MODAL DE CREACIÓN / EDICIÓN DE REPROCESO (CREATEPORTAL)
      ========================================================================= */}
      {isFormOpen && createPortal(
        <div className="modal-backdrop-custom animate-fadeIn" onClick={() => setIsFormOpen(false)}>
          <div className="detail-modal-card animate-fadeIn" onClick={e => e.stopPropagation()} style={{ maxWidth: "860px" }}>
            <div className="detail-modal-header" style={{ background: "linear-gradient(135deg, #6D28D9 0%, #7C3AED 100%)", color: "white", padding: "16px 24px" }}>
              <div className="detail-modal-title-box">
                <div style={{ display: "flex", gap: "6px", marginBottom: "4px" }}>
                  <span className="badge-modal-tag" style={{ background: "rgba(255,255,255,0.2)", color: "white" }}>SECCIÓN VI</span>
                  <span className="badge-modal-tag" style={{ background: "#EDE9FE", color: "#6D28D9", fontWeight: 700 }}>REPROCESO & ELABORACIÓN DE CLASES</span>
                </div>
                <h1 className="detail-modal-title" style={{ color: "white", fontSize: "19px" }}>
                  {formData?.id_bitacora_reproceso || formData?.dbId ? "Editar Reproceso en Envasado" : "Nuevo Registro de Reproceso (Envasado)"}
                </h1>
                <p style={{ margin: 0, fontSize: "12px", opacity: 0.9 }}>
                  Transformación y reacondicionamiento de lotes a granel conforme a la NOM-070-SCFI-2016
                </p>
              </div>
              <button type="button" onClick={() => setIsFormOpen(false)} className="detail-modal-close-btn">
                <CloseIcon />
              </button>
            </div>

            <form onSubmit={e => handleSubmit(e, "enviada")} style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: "16px", maxHeight: "80vh", overflowY: "auto" }}>
              
              {/* 1. LOTE A GRANEL DE ORIGEN */}
              <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "14px", padding: "18px" }}>
                <h4 style={{ margin: "0 0 12px 0", fontSize: "12px", fontWeight: 800, color: "#6D28D9", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  1. Selección del Lote a Granel de Origen (Envasado)
                </h4>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div className="integrated-input-box">
                    <span className="integrated-input-label">Lote a Granel Origen <span style={{ color: "#DC2626" }}>*</span></span>
                    <select
                      value={formData?.id_bitacora_granel || formData?.loteOrigen || ""}
                      onChange={handleLoteOrigenChange}
                      className="integrated-select-control"
                      required
                    >
                      <option value="">-- Seleccionar Lote a Granel ({availableGraneles.length} Disponibles) --</option>
                      {availableGraneles.map((g, gIdx) => {
                        const v = g._vDisp ?? (parseFloat(String(g.invFinalVol ?? g.litros ?? 0).replace(/[^\d.]/g, '')) || 0);
                        const a = g._aDisp ?? (parseFloat(String(g.porcentaje_alc_vol ?? 0).replace(/[^\d.]/g, '')) || 0);
                        const val = g.id_bitacora_granel || g.id || g.dbId || g.noLote || g.no_lote;
                        const label = `${g.noLote || g.no_lote || 'Lote'} - ${g.categoria || 'Mezcal'} | ${g.clase || 'Blanco'} (Disp: ${v.toLocaleString()} L @ ${a}%)`;
                        return (
                          <option key={val || gIdx} value={val}>
                            {label}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div className="integrated-input-box">
                    <span className="integrated-input-label">Fecha de Ingreso al Proceso</span>
                    <input
                      type="date"
                      name="fechaIngreso"
                      value={formData?.fechaIngreso || ""}
                      onChange={handleChange}
                      className="integrated-input-control"
                      required
                    />
                  </div>
                </div>

                {/* Parámetros heredados */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "10px", marginTop: "12px", background: "#FFFFFF", padding: "12px", borderRadius: "10px", border: "1px solid #E2E8F0" }}>
                  <div>
                    <span style={{ fontSize: "11px", color: "#64748B", display: "block" }}>Volumen Origen:</span>
                    <strong style={{ fontSize: "14px", color: "#1E293B" }}>{Number(formData?.volumenOrigen || 0).toLocaleString()} L</strong>
                  </div>
                  <div>
                    <span style={{ fontSize: "11px", color: "#64748B", display: "block" }}>% Alc. Vol. Origen:</span>
                    <strong style={{ fontSize: "14px", color: "#1E293B" }}>{formData?.pctAlcOrigen || 0}%</strong>
                  </div>
                  <div>
                    <span style={{ fontSize: "11px", color: "#64748B", display: "block" }}>Categoría:</span>
                    <strong style={{ fontSize: "13px", color: "#1E293B" }}>{formData?.categoriaOrigen || "Mezcal Artesanal"}</strong>
                  </div>
                  <div>
                    <span style={{ fontSize: "11px", color: "#64748B", display: "block" }}>Clase Origen:</span>
                    <strong style={{ fontSize: "13px", color: "#1E293B" }}>{formData?.claseOrigen || "Blanco Joven"}</strong>
                  </div>
                  <div>
                    <span style={{ fontSize: "11px", color: "#64748B", display: "block" }}>Maguey / Especie:</span>
                    <strong style={{ fontSize: "12px", color: "#1E293B" }}>{formData?.tipoMagueyOrigen || "No especificado"}</strong>
                  </div>
                </div>
              </div>

              {/* 2. PROCESAMIENTO DEL MEZCAL */}
              <div style={{ background: "#EDE9FE", border: "1px solid #DDD6FE", borderRadius: "14px", padding: "18px" }}>
                <h4 style={{ margin: "0 0 12px 0", fontSize: "12px", fontWeight: 800, color: "#6D28D9", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  2. Procesamiento del Mezcal (NOM-070-SCFI-2016)
                </h4>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div className="integrated-input-box">
                    <span className="integrated-input-label">Proceso Realizado <span style={{ color: "#DC2626" }}>*</span></span>
                    <select
                      name="procesoRealizado"
                      value={formData?.procesoRealizado || "REPROCESO O REACONDICIONAMIENTO"}
                      onChange={handleChange}
                      className="integrated-select-control"
                      style={{ fontWeight: 700, color: "#6D28D9" }}
                      required
                    >
                      <option value="REPROCESO O REACONDICIONAMIENTO">REPROCESO O REACONDICIONAMIENTO</option>
                      <option value="ABOCADO CON">ABOCADO CON</option>
                      <option value="DESTILADO CON">DESTILADO CON</option>
                    </select>
                  </div>

                  <div className="integrated-input-box">
                    <span className="integrated-input-label">Ingredientes Adicionados (Orden Cuantitativo)</span>
                    <input
                      type="text"
                      name="ingredientesAdicionados"
                      value={formData?.ingredientesAdicionados || ""}
                      onChange={handleChange}
                      placeholder="Ej: Insectos, frutas, miel, botánicos si aplica..."
                      className="integrated-input-control"
                    />
                  </div>
                </div>

                <div className="integrated-input-box" style={{ marginTop: "12px" }}>
                  <span className="integrated-input-label">Descripción del Proceso / Procedimiento</span>
                  <textarea
                    name="descripcionProceso"
                    value={formData?.descripcionProceso || ""}
                    onChange={handleChange}
                    placeholder="Detalles sobre maceración, tiempo de contacto, adición o filtración..."
                    className="integrated-input-control"
                    style={{ minHeight: "50px", resize: "vertical" }}
                  />
                </div>
              </div>

              {/* 3. RESULTADO DEL REPROCESO */}
              <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: "14px", padding: "18px" }}>
                <h4 style={{ margin: "0 0 12px 0", fontSize: "12px", fontWeight: 800, color: "#15803D", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  3. Lote Resultante & Salida a Granel
                </h4>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div className="integrated-input-box">
                    <span className="integrated-input-label">Lote Salida Resultante <span style={{ color: "#DC2626" }}>*</span></span>
                    <input
                      type="text"
                      name="loteSalida"
                      value={formData?.loteSalida || ""}
                      onChange={handleChange}
                      placeholder="Ej: LOTE-ENV-REP-01"
                      className="integrated-input-control"
                      style={{ fontWeight: 800, color: "#047857" }}
                      required
                    />
                  </div>

                  <div className="integrated-input-box">
                    <span className="integrated-input-label">Fecha de Salida / Terminación</span>
                    <input
                      type="date"
                      name="fechaSalida"
                      value={formData?.fechaSalida || ""}
                      onChange={handleChange}
                      className="integrated-input-control"
                      required
                    />
                  </div>

                  <div className="integrated-input-box">
                    <span className="integrated-input-label">Volumen Final (Litros) <span style={{ color: "#DC2626" }}>*</span></span>
                    <input
                      type="number"
                      step="any"
                      name="volumenSalida"
                      value={formData?.volumenSalida || ""}
                      onChange={handleChange}
                      placeholder="Ej: 500"
                      className="integrated-input-control"
                      required
                    />
                    <span className="integrated-input-suffix">L</span>
                  </div>

                  <div className="integrated-input-box">
                    <span className="integrated-input-label">% Alc. Vol. Final</span>
                    <input
                      type="number"
                      step="0.1"
                      name="pctAlcSalida"
                      value={formData?.pctAlcSalida || ""}
                      onChange={handleChange}
                      placeholder="Ej: 46.5"
                      className="integrated-input-control"
                    />
                    <span className="integrated-input-suffix">%</span>
                  </div>
                </div>

                <div style={{ marginTop: "12px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div className="integrated-input-box" style={{ background: "#DCFCE7", borderColor: "#86EFAC" }}>
                    <span className="integrated-input-label" style={{ color: "#15803D" }}>Clase Final Conforme a NOM-070</span>
                    <div style={{ fontSize: "16px", fontWeight: 800, color: "#166534", padding: "6px 0" }}>
                      {formData?.claseSalida || obtenerClaseFinalSegunProceso(formData?.procesoRealizado || "REPROCESO O REACONDICIONAMIENTO", formData?.claseOrigen)}
                    </div>
                  </div>

                  <div className="integrated-input-box">
                    <span className="integrated-input-label">Observaciones</span>
                    <input
                      type="text"
                      name="observaciones"
                      value={formData?.observaciones || ""}
                      onChange={handleChange}
                      placeholder="Notas adicionales o justificación técnica..."
                      className="integrated-input-control"
                    />
                  </div>
                </div>
              </div>

              {/* BOTONES DE ACCIÓN */}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "10px" }}>
                <button
                  type="button"
                  onClick={() => setIsFormOpen(false)}
                  className="btn-cancel"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={(e) => handleSubmit(e, "borrador")}
                  style={{ background: "#F1F5F9", color: "#334155", border: "1px solid #CBD5E1", padding: "10px 18px", borderRadius: "10px", fontWeight: 700, cursor: "pointer" }}
                >
                  Guardar Borrador
                </button>
                <button
                  type="submit"
                  style={{ background: "linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)", color: "white", border: "none", padding: "10px 22px", borderRadius: "10px", fontWeight: 800, cursor: "pointer", boxShadow: "0 4px 12px rgba(124, 58, 237, 0.3)" }}
                >
                  Enviar a Revisión UI
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* =========================================================================
          MODAL DE DETALLE DE REPROCESO (CREATEPORTAL)
      ========================================================================= */}
      {selectedRecord && createPortal(
        <div className="modal-backdrop-custom animate-fadeIn" onClick={() => setSelectedRecord(null)}>
          <div className="detail-modal-card animate-fadeIn" onClick={e => e.stopPropagation()} style={{ maxWidth: "700px" }}>
            <div className="detail-modal-header" style={{ background: "linear-gradient(135deg, #6D28D9 0%, #7C3AED 100%)", color: "white", padding: "16px 22px" }}>
              <div className="detail-modal-title-box">
                <span className="badge-modal-tag" style={{ background: "rgba(255,255,255,0.2)", color: "white", marginBottom: "4px", display: "inline-block" }}>
                  DETALLE DE REPROCESO
                </span>
                <h1 className="detail-modal-title" style={{ color: "white", fontSize: "19px" }}>
                  Lote Salida: {selectedRecord.loteSalida || selectedRecord.lote_salida || "-"}
                </h1>
                <p style={{ margin: 0, fontSize: "12px", opacity: 0.9 }}>
                  Origen: {selectedRecord.loteOrigen || selectedRecord.lote_origen} • Proceso: {selectedRecord.procesoRealizado || selectedRecord.proceso_realizado}
                </p>
              </div>
              <button type="button" onClick={() => setSelectedRecord(null)} className="detail-modal-close-btn">
                <CloseIcon />
              </button>
            </div>

            <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: "16px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
                <div style={{ background: "#F8FAFC", padding: "14px", borderRadius: "12px", border: "1px solid #E2E8F0" }}>
                  <h5 style={{ margin: "0 0 10px 0", color: "#6D28D9", fontSize: "12px", textTransform: "uppercase", fontWeight: 800 }}>Datos de Origen</h5>
                  <div style={{ fontSize: "13px", lineHeight: "1.6" }}>
                    <div><b>Lote:</b> {selectedRecord.loteOrigen || selectedRecord.lote_origen}</div>
                    <div><b>Volumen:</b> {Number(selectedRecord.volumenOrigen || selectedRecord.volumen_origen || 0).toLocaleString()} L</div>
                    <div><b>% Alc. Vol.:</b> {selectedRecord.pctAlcOrigen || selectedRecord.pct_alc_origen}%</div>
                    <div><b>Categoría:</b> {selectedRecord.categoriaOrigen || selectedRecord.categoria_origen}</div>
                    <div><b>Clase:</b> {selectedRecord.claseOrigen || selectedRecord.clase_origen}</div>
                    <div><b>Maguey:</b> {selectedRecord.tipoMagueyOrigen || selectedRecord.tipo_maguey_origen || "-"}</div>
                  </div>
                </div>

                <div style={{ background: "#F0FDF4", padding: "14px", borderRadius: "12px", border: "1px solid #BBF7D0" }}>
                  <h5 style={{ margin: "0 0 10px 0", color: "#15803D", fontSize: "12px", textTransform: "uppercase", fontWeight: 800 }}>Datos de Salida</h5>
                  <div style={{ fontSize: "13px", lineHeight: "1.6" }}>
                    <div><b>Lote Resultante:</b> <span style={{ color: "#047857", fontWeight: 800 }}>{selectedRecord.loteSalida || selectedRecord.lote_salida}</span></div>
                    <div><b>Volumen Resultante:</b> {Number(selectedRecord.volumenSalida || selectedRecord.volumen_salida || 0).toLocaleString()} L</div>
                    <div><b>% Alc. Vol.:</b> {selectedRecord.pctAlcSalida || selectedRecord.pct_alc_salida}%</div>
                    <div><b>Clase Salida (NOM-070):</b> <span style={{ color: "#7C3AED", fontWeight: 800 }}>{selectedRecord.claseSalida || selectedRecord.clase_salida}</span></div>
                    <div><b>Fecha Salida:</b> {selectedRecord.fechaSalida || selectedRecord.fecha_salida || "-"}</div>
                  </div>
                </div>
              </div>

              <div style={{ background: "#EDE9FE", padding: "14px", borderRadius: "12px", border: "1px solid #DDD6FE" }}>
                <h5 style={{ margin: "0 0 8px 0", color: "#6D28D9", fontSize: "12px", textTransform: "uppercase", fontWeight: 800 }}>Proceso Realizado</h5>
                <div style={{ fontSize: "13px", lineHeight: "1.6" }}>
                  <div><b>Proceso:</b> {selectedRecord.procesoRealizado || selectedRecord.proceso_realizado}</div>
                  <div><b>Ingredientes Adicionados:</b> {selectedRecord.ingredientesAdicionados || selectedRecord.ingredientes_adicionados || "Ninguno"}</div>
                  <div><b>Descripción del Proceso:</b> {selectedRecord.descripcionProceso || selectedRecord.descripcion_proceso || "Sin descripción"}</div>
                </div>
              </div>

              {selectedRecord.observaciones_correccion && (
                <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", padding: "12px", borderRadius: "10px", color: "#991B1B", fontSize: "13px" }}>
                  <b>Observaciones del Inspector UI:</b> {selectedRecord.observaciones_correccion}
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={() => setSelectedRecord(null)}
                  style={{ background: "#6D28D9", color: "white", border: "none", padding: "8px 20px", borderRadius: "8px", fontWeight: 700, cursor: "pointer" }}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ReprocesoSection;
